/**
 * Пресеты и хелперы периода-диапазона для общего PeriodPicker (дашборд,
 * платежи). Диапазон — ISO-строки `from`/`to` включительно.
 */

import dayjs from 'dayjs';

export type Range = {from: string; to: string};

const iso = (d: dayjs.Dayjs): string => d.format('YYYY-MM-DD');

export type PeriodPreset = {key: string; label: string; range: (now?: Date) => Range};

export const PERIOD_PRESETS: PeriodPreset[] = [
  {
    key: 'last7',
    label: '7 дней',
    range: (n) => ({from: iso(dayjs(n).subtract(6, 'day')), to: iso(dayjs(n))}),
  },
  {
    key: 'last30',
    label: '30 дней',
    range: (n) => ({from: iso(dayjs(n).subtract(29, 'day')), to: iso(dayjs(n))}),
  },
  {
    key: 'thisMonth',
    label: 'Этот месяц',
    range: (n) => ({from: iso(dayjs(n).startOf('month')), to: iso(dayjs(n).endOf('month'))}),
  },
  {
    key: 'lastMonth',
    label: 'Прошлый месяц',
    range: (n) => {
      const m = dayjs(n).subtract(1, 'month');
      return {from: iso(m.startOf('month')), to: iso(m.endOf('month'))};
    },
  },
  {
    key: 'quarter',
    label: 'Квартал',
    range: (n) => {
      const d = dayjs(n);
      const q = Math.floor(d.month() / 3) * 3;
      return {from: iso(d.month(q).startOf('month')), to: iso(d.month(q + 2).endOf('month'))};
    },
  },
  {
    key: 'year',
    label: 'Год',
    range: (n) => ({from: iso(dayjs(n).startOf('year')), to: iso(dayjs(n).endOf('year'))}),
  },
];

export const DEFAULT_RANGE = (now?: Date): Range => PERIOD_PRESETS[2].range(now);

export const rangeDays = (r: Range): number => dayjs(r.to).diff(dayjs(r.from), 'day') + 1;

const pluralDays = (n: number): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня';
  return 'дней';
};

/** «1 июн — 2 июл» — короткая подпись диапазона. */
export const rangeLabel = (r: Range): string =>
  `${dayjs(r.from).format('D MMM')} — ${dayjs(r.to).format('D MMM')}`;

/** «1 июн — 2 июл · 32 дня» — с числом дней (для шапки пикера). */
export const rangeLabelFull = (r: Range): string => {
  const n = rangeDays(r);
  return `${rangeLabel(r)} · ${n} ${pluralDays(n)}`;
};

export const matchPreset = (r: Range, now?: Date): string | null =>
  PERIOD_PRESETS.find((p) => {
    const pr = p.range(now);
    return pr.from === r.from && pr.to === r.to;
  })?.key ?? null;
